import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const QUESTIONS = [
  {
    q: "Which LangChain abstraction lets you build stateful, multi-step agent workflows as graphs?",
    options: ["LangChain Agents", "LangGraph", "LangServe", "LangSmith"],
    answer: 1,
  },
  {
    q: "What does pgvector add to PostgreSQL?",
    options: ["Vector storage and similarity queries", "Full-text search via BM25", "Column-level encryption", "Time-series partitioning"],
    answer: 0,
  },
  {
    q: "In FastAPI, which decorator registers a POST route at /items?",
    options: ["@router.route('/items')", "@app.handle('/items', 'POST')", "@app.post('/items')", "@app.register('/items')"],
    answer: 2,
  },
  {
    q: "What is the primary purpose of Redis in a RAG pipeline?",
    options: ["Storing the vector index permanently", "Running the embedding model locally", "Persisting conversation history to disk", "Caching frequent query results to cut latency"],
    answer: 3,
  },
  {
    q: "In AWS Bedrock, 'foundation models' refers to:",
    options: ["Pre-trained large models from Anthropic, Meta, Mistral and others", "Your own fine-tuned models only", "AWS's internal GPT-4 equivalent", "Models trained exclusively on AWS data"],
    answer: 0,
  },
  {
    q: "Kafka's concept of 'consumer group' means:",
    options: ["Multiple brokers replicating a topic", "Multiple consumers sharing a topic's partitions so each message is processed once", "A cluster of Kafka servers", "An access control group for producers"],
    answer: 1,
  },
  {
    q: "What distinguishes Docker containers from virtual machines?",
    options: ["Containers have their own kernel per instance", "Containers run slower but use less disk", "Containers share the host OS kernel — they're just isolated processes", "VMs can't run on Linux hosts"],
    answer: 2,
  },
];

export default function TechStackTrivia({ onDone }) {
  const [idx, setIdx]       = useState(0);
  const [score, setScore]   = useState(0);
  const [picked, setPicked] = useState(null);

  const round = QUESTIONS[idx];

  const choose = (i) => {
    if (picked !== null) return;
    const correct = i === round.answer;
    if (correct) setScore(s => s + 1);
    setPicked({ i, correct });
  };

  const next = () => {
    if (idx + 1 >= QUESTIONS.length) { onDone(score); return; }
    setIdx(i => i + 1);
    setPicked(null);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, color: "var(--text-muted)", fontSize: "0.8rem" }}>
        <span>Question {idx + 1}/{QUESTIONS.length}</span>
        <span>Score: {score}</span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
          <p style={{ fontSize: "0.95rem", color: "var(--text)", marginBottom: 20, lineHeight: 1.6 }}>{round.q}</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {round.options.map((opt, i) => {
              const isCorrect = picked && i === round.answer;
              const isWrong   = picked && picked.i === i && !picked.correct;
              let borderColor = "var(--border)";
              let bg = "var(--bg-card)";
              if (isCorrect) { borderColor = "rgba(34,197,94,0.5)"; bg = "rgba(34,197,94,0.08)"; }
              if (isWrong)   { borderColor = "rgba(239,68,68,0.5)";  bg = "rgba(239,68,68,0.08)"; }

              return (
                <div key={i} onClick={() => choose(i)} style={{
                  padding: "12px 16px", borderRadius: 10, fontSize: "0.875rem",
                  border: `1px solid ${borderColor}`, background: bg,
                  cursor: picked ? "default" : "pointer", color: "var(--text)",
                  transition: "border-color 120ms ease, background 120ms ease",
                  display: "flex", alignItems: "center", gap: 10,
                }}
                  onMouseEnter={e => { if (!picked) { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.background = "var(--bg-subtle)"; } }}
                  onMouseLeave={e => { if (!picked) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg-card)"; } }}
                >
                  <span style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "var(--text-muted)", minWidth: 16 }}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  {opt}
                  {isCorrect && <span style={{ marginLeft: "auto", color: "#22c55e", fontSize: "0.75rem", fontWeight: 600 }}>correct</span>}
                  {isWrong   && <span style={{ marginLeft: "auto", color: "#ef4444", fontSize: "0.75rem", fontWeight: 600 }}>wrong</span>}
                </div>
              );
            })}
          </div>

          {picked && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ marginTop: 16 }}>
              <button className="btn-pill" onClick={next} style={{ width: "100%", justifyContent: "center" }}>
                {idx + 1 >= QUESTIONS.length ? "See Results" : "Next →"}
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
